/** Shared enums, labels and styling for the eval rubric pages (PROC-187). */

/** Mirrors ``EvalRubric.Status`` in api-django. */
export const RubricStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  RETIRED: "retired",
} as const;

/**
 * The built-in criteria, in display order.
 *
 * Groundedness comes first because it is scored separately from the quality
 * criteria — an output can be perfectly grounded and still useless, and
 * averaging the two hides exactly that.
 *
 * Labels and ordering only. What is actually scored comes from the server's
 * `rows`, which also carries criteria someone added — those have keys minted
 * at creation, so no enum can enumerate them and the backend field
 * deliberately has no `choices`.
 */
export const BuiltInCriterion = {
  GROUNDEDNESS: "groundedness",
  CORRECTNESS: "correctness",
  FORMATTING: "formatting",
  USEFULNESS: "usefulness",
  COMPLETENESS: "completeness",
  PERTINENCE: "pertinence",
  INSTRUCTION_FOLLOWING: "instruction_following",
  TONE: "tone",
} as const;

export type BuiltInCriterionKey = (typeof BuiltInCriterion)[keyof typeof BuiltInCriterion];

export const DIMENSIONS: ReadonlyArray<{ value: BuiltInCriterionKey; label: string }> = [
  { value: BuiltInCriterion.GROUNDEDNESS, label: "Groundedness" },
  { value: BuiltInCriterion.CORRECTNESS, label: "Correctness" },
  { value: BuiltInCriterion.FORMATTING, label: "Formatting" },
  { value: BuiltInCriterion.USEFULNESS, label: "Usefulness" },
  { value: BuiltInCriterion.COMPLETENESS, label: "Completeness" },
  { value: BuiltInCriterion.PERTINENCE, label: "Right for this child" },
  { value: BuiltInCriterion.INSTRUCTION_FOLLOWING, label: "Instruction following" },
  { value: BuiltInCriterion.TONE, label: "Tone" },
];

/** What each criterion is asking, shown beneath its row. */
export const DIMENSION_HINTS: Record<string, string> = {
  [BuiltInCriterion.GROUNDEDNESS]: "Is every claim traceable to something in the source documents?",
  [BuiltInCriterion.CORRECTNESS]: "Is it accurate, clinically and factually?",
  [BuiltInCriterion.FORMATTING]:
    "Is it shaped the way the prompt asked, with no remarks about the content and nothing addressed to a reader?",
  [BuiltInCriterion.USEFULNESS]: "Would a BCBA keep this, or rewrite it?",
  [BuiltInCriterion.COMPLETENESS]: "Does it cover what the field requires, without padding?",
  [BuiltInCriterion.PERTINENCE]:
    "Is this right for this child, reading assessment scores, the parent interview, observation and the behavior plan together?",
  [BuiltInCriterion.INSTRUCTION_FOLLOWING]: "Does the output obey the prompt it was given?",
  [BuiltInCriterion.TONE]: "Clinical register, third person, strengths-based, person-first.",
};

/**
 * Status chip styling.
 *
 * Rectangular, not pill: the design system reserves full-round for avatars
 * and dots. ACTIVE uses the positive pairing it names — Green 700 ink on
 * Green 100 — since no semantic success *fill* token is exported.
 */
export const STATUS_STYLES: Record<string, string> = {
  [RubricStatus.DRAFT]: "bg-bg-default text-text-secondary",
  [RubricStatus.ACTIVE]: "bg-green-100 text-green-700",
  [RubricStatus.RETIRED]: "bg-bg-default text-text-tertiary",
};

/**
 * A criterion's key as it appears in a score.
 *
 * Either a built-in key or a slug minted when someone added a criterion, so
 * it cannot be narrowed to a union — `string` is the honest type, and the
 * backend field has no `choices` for the same reason.
 */
export type CriterionKey = string;

/**
 * Server-sent criterion keys, in display order.
 *
 * Built-ins first in `DIMENSIONS` order, then anything added, in the order
 * the server sent it. Filtering *to* `DIMENSIONS` instead would silently drop
 * added criteria — which is the one thing "New criteria" exists to do.
 */
export const orderCriteria = (keys: readonly string[]): CriterionKey[] => {
  const present = new Set(keys);
  const builtIn = DIMENSIONS.filter((d) => present.has(d.value)).map((d) => d.value as CriterionKey);
  const added = keys.filter((key) => !DIMENSIONS.some((d) => d.value === key));
  return [...builtIn, ...added];
};

/** A criterion's label: its built-in name, or its key made readable. */
export const criterionLabel = (key: string): string =>
  DIMENSIONS.find((d) => d.value === key)?.label ?? key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
